import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className={`text-xl md:text-2xl font-heading font-bold tracking-wider whitespace-nowrap ${
              isScrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            掌中之物
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? isScrolled || !isHome
                    ? 'text-primary-700 bg-primary-100'
                    : 'text-background-50 bg-white/20'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              首页
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith('/category')
                    ? isScrolled || !isHome
                      ? 'text-primary-700 bg-primary-100'
                      : 'text-background-50 bg-white/20'
                    : isScrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                精品分类
                <i className={`ri-arrow-down-s-line transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-48 bg-background-50 rounded-lg border border-background-200/70 py-1 z-50">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/wenwan-panwan-rumen"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname.startsWith('/article')
                  ? isScrolled || !isHome
                    ? 'text-primary-700 bg-primary-100'
                    : 'text-background-50 bg-white/20'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              精选文章
            </Link>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? isScrolled || !isHome
                    ? 'text-primary-700 bg-primary-100'
                    : 'text-background-50 bg-white/20'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>

            <Link
              to="/about"
              className="ml-3 px-5 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              联系我们
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link to="/" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer">首页</Link>
            <div className="px-4 py-2.5 text-sm font-semibold text-foreground-500">精品分类</div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="pl-8 pr-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/article/wenwan-panwan-rumen" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer">精选文章</Link>
            <Link to="/about" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer">关于我们</Link>
            <Link to="/about" className="px-4 py-2.5 mt-1 bg-primary-500 text-background-50 rounded-lg text-sm font-medium text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">联系我们</Link>
          </div>
        </div>
      )}
    </nav>
  );
}